import mapboxgl from 'mapbox-gl';

import { toast } from '../toastService';

/**
 * Raio por Deslocamento (isócrona) e Régua, compartilhados entre os mapas Mapbox.
 * As duas ferramentas vivem juntas porque são mutuamente exclusivas: cada uma disputa
 * o clique no mapa, e ligar uma precisa desligar a outra.
 *
 * Contrato do hospedeiro:
 *  - `self.map`, `self.draw`, `self.drawExtras`, `self.ruler`, `self.state`, `self.setState`;
 *  - `self.buscarPelaAreaDesenhada()` (e, opcionalmente, `self.applyShapeFilter()`);
 *  - `self.handleIsochroneCenterPick` como método de instância, porque o listener
 *    precisa da MESMA referência para poder ser removido com `map.off`;
 *  - espalhar `ISOCHRONE_INITIAL_STATE` no estado inicial.
 */

export const ISOCHRONE_INITIAL_STATE = {
    showRaioDeslocamento: false,
    pickingIsochroneCenter: false,
    isochroneCenter: null,
};

// ===== Régua (medição de distância) =====

/** Liga/desliga a régua. Ao desligar, o traçado é apagado. */
export function toggleRuler(self) {
    if (!self.ruler) return;

    if (self.ruler.active) {
        deactivateRuler(self);
        return;
    }

    // Medir e desenhar/escolher ponto de partida são exclusivos.
    self.draw?.changeMode('simple_select');
    if (self.state.pickingIsochroneCenter) toggleIsochronePick(self);

    self.ruler.activate();
    toast.info('Clique no mapa para medir. Duplo clique encerra a medição, Esc limpa.');
}

export function deactivateRuler(self) {
    if (self.ruler?.active) self.ruler.deactivate();
}

// ===== Raio por Deslocamento (isócrona) =====

/**
 * Ativa o modo de escolha do ponto de partida: o próximo clique no mapa define o
 * centro e abre o modal. Clicar de novo no botão cancela a escolha.
 */
export function toggleIsochronePick(self) {
    if (!self.map) return;

    if (self.state.pickingIsochroneCenter) {
        self.map.off('click', self.handleIsochroneCenterPick);
        self.map.getCanvas().style.cursor = '';
        self.drawExtras?.setIsochroneActive(false);
        self.setState({ pickingIsochroneCenter: false });
        return;
    }

    // Sair de qualquer modo de desenho evita que o clique do ponto de partida
    // acabe virando um vértice de forma; a régua também disputaria o clique.
    self.draw?.changeMode('simple_select');
    deactivateRuler(self);

    self.drawExtras?.setIsochroneActive(true);
    self.setState({ pickingIsochroneCenter: true });
    self.map.getCanvas().style.cursor = 'crosshair';
    self.map.once('click', self.handleIsochroneCenterPick);
    toast.info('Clique no mapa para definir o ponto de partida do deslocamento.');
}

export function handleIsochroneCenterPick(self, e) {
    self.map.getCanvas().style.cursor = '';
    self.drawExtras?.setIsochroneActive(false);
    self.setState({
        pickingIsochroneCenter: false,
        isochroneCenter: { lat: e.lngLat.lat, lng: e.lngLat.lng },
        showRaioDeslocamento: true,
    });
}

/**
 * Adiciona as isócronas ao draw como formas (recortam os marcadores como qualquer
 * polígono desenhado), registra na lista de formas e roda a busca por área de cada uma.
 */
export async function handleIsochronesReady(self, isochrones, tipoLabel) {
    if (!self.draw) return;

    const newShapes = [];
    const allBounds = new mapboxgl.LngLatBounds();
    const extendBounds = (coords) => {
        if (typeof coords[0] === 'number') { allBounds.extend(coords); return; }
        coords.forEach(extendBounds);
    };

    isochrones.forEach(({ feature, tempo, cor }, i) => {
        const polygon = {
            type: 'Feature',
            properties: { shapeType: 'isochrone' },
            geometry: feature.geometry,
        };
        const [drawId] = self.draw.add(polygon);

        const name = `Deslocamento ${tipoLabel} em ${tempo} minutos`;
        const areaId = `drawn-${Date.now()}-${i}`;
        // Paridade com o mapa Google: isócrona sem preenchimento, só o contorno colorido.
        const styleAttrs = { fillColor: cor, fillOpacity: 0, strokeColor: cor, strokeWeight: 4 };
        Object.entries(styleAttrs).forEach(([k, v]) => self.draw.setFeatureProperty(drawId, k, v));

        extendBounds(feature.geometry.coordinates || []);

        newShapes.push({
            drawId, areaId, type: 'Isócrona', name, color: cor, visible: true,
            feature: self.draw.get(drawId), center: null, radiusMeters: null, ...styleAttrs,
        });
    });

    await new Promise((resolve) =>
        self.setState((prev) => ({ drawnShapes: [...prev.drawnShapes, ...newShapes] }), resolve)
    );

    if (!allBounds.isEmpty()) {
        self.map.fitBounds(allBounds, { padding: 60, duration: 600 });
    }

    // Nem todo mapa recorta os marcadores no cliente (o de Revenda recorta ao
    // recarregar os clusters), então o filtro é opcional.
    self.applyShapeFilter?.();
    // Isócrona é uma área desenhada como as outras: já traz os resultados dela.
    await self.buscarPelaAreaDesenhada();
}
